import { io, type Socket } from "socket.io-client";
import { logger } from "../logger";

export interface LocationData {
  lat: number;
  lng: number;
  accuracy?: number | null;
  altitude?: number | null;
  heading?: number | null;
  speed?: number | null;
  timestamp?: number;
}

export interface HeartbeatData {
  operatorId: string;
  location: LocationData;
  status: string;
  isTracking: boolean;
  timestamp: number;
}

export const SOCKET_EVENTS = {
  TRIP: {
    UPDATED: "trip:updated",
  },
  OPERATOR: {
    HEARTBEAT: "operator:heartbeat",
  },
  SUBSCRIBE: "subscribe",
  UNSUBSCRIBE: "unsubscribe",
} as const;

class SocketService {
  private static instance: SocketService;
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 20; // Further increased for more resilience
  private reconnectDelay = 1000; // Start with 1 second
  private subscriptions = new Set<string>();
  private connectionToken: string | null = null;
  private heartbeatInterval: number | null = null;
  private connectionMonitorInterval: number | null = null;
  private pendingCallbacks = {
    heartbeat: new Set<(data: HeartbeatData) => void>(),
  };

  private constructor() {
    // constructor is private to prevent instantiation
  }

  static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  connect(token: string) {
    // Store token for reconnection purposes
    this.connectionToken = token;

    if (this.socket?.connected) {
      logger.debug("[SocketService] Socket already connected");
      this.startHeartbeat();
      return this.socket;
    }

    // Close any existing socket before creating a new one
    if (this.socket) {
      logger.debug(
        "[SocketService] Closing existing socket before reconnecting"
      );
      this.socket.close();
      this.socket = null;
    }

    logger.debug("[SocketService] Initializing socket connection...", {
      url: import.meta.env.VITE_API_URL,
      token: token ? "present" : "missing",
    });

    try {
      this.socket = io(import.meta.env.VITE_API_URL, {
        auth: { token },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
        timeout: 30000, // Increased timeout to 30 seconds
        autoConnect: true,
        reconnectionDelayMax: 30000,
      });

      this.setupEventHandlers();
      this.startConnectionMonitor();
      this.startHeartbeat();

      return this.socket;
    } catch (error) {
      logger.error("[SocketService] Error initializing socket:", error);
      return null;
    }
  }

  private startHeartbeat() {
    // Clear any existing heartbeat
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // Set up a new heartbeat interval (every 30 seconds)
    this.heartbeatInterval = window.setInterval(() => {
      if (this.socket?.connected) {
        logger.debug("[SocketService] Sending heartbeat ping");
        this.socket.emit("ping", { timestamp: Date.now() });
      } else {
        logger.warn(
          "[SocketService] Heartbeat check failed - socket not connected"
        );
        this.reconnectIfNeeded();
      }
    }, 30000);
  }

  private startConnectionMonitor() {
    // Clear any existing monitor
    if (this.connectionMonitorInterval) {
      clearInterval(this.connectionMonitorInterval);
    }

    // Check connection status every 10 seconds
    this.connectionMonitorInterval = window.setInterval(() => {
      if (!this.socket?.connected) {
        logger.warn(
          "[SocketService] Connection monitor detected disconnected socket"
        );
        this.reconnectIfNeeded();
      }
    }, 10000);
  }

  private reconnectIfNeeded() {
    if (!this.socket?.connected && this.connectionToken) {
      logger.debug("[SocketService] Reconnecting with existing token");
      this.connect(this.connectionToken);
    }
  }

  private setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      logger.debug("[SocketService] Connected - socketId:", this.socket?.id);
      this.reconnectAttempts = 0;
      this.resubscribeAll(); // Resubscribe to all previous subscriptions
      this.reattachAllListeners(); // Reattach all event listeners
    });

    this.socket.on("disconnect", (reason) => {
      logger.debug("[SocketService] Disconnected:", reason);

      // Handle different disconnect reasons appropriately
      if (reason === "io server disconnect") {
        // The server has forcefully disconnected the socket, need to manually reconnect
        logger.warn(
          "[SocketService] Server disconnected the socket. Attempting manual reconnection."
        );
        this.reconnectIfNeeded();
      } else if (reason === "io client disconnect") {
        // The socket was manually disconnected, don't reconnect
        logger.debug(
          "[SocketService] Client initiated disconnect. Not reconnecting."
        );
      } else {
        // For transport-related issues, socket.io should automatically reconnect
        logger.debug(
          "[SocketService] Transport-level disconnect. Socket.IO will attempt to reconnect automatically."
        );

        // Still check after a delay to make sure reconnection happened
        setTimeout(() => {
          if (!this.socket?.connected) {
            logger.warn(
              "[SocketService] Automatic reconnection failed, attempting manual reconnection"
            );
            this.reconnectIfNeeded();
          }
        }, 5000);
      }
    });

    this.socket.on("connect_error", (error) => {
      logger.error("[SocketService] Connection error:", error);
      this.handleReconnect();
    });

    this.socket.on("error", (error) => {
      logger.error("[SocketService] Socket error:", error);
    });

    // Add handler for pong response from server
    this.socket.on("pong", (data) => {
      logger.debug("[SocketService] Received heartbeat pong from server", data);
    });

    // Debug: log all events with more detail
    this.socket.onAny((event, ...args) => {
      if (event !== "ping" && event !== "pong") {
        // Don't log heartbeat events
        logger.debug(
          "[SocketService] Event:",
          event,
          "Data:",
          JSON.stringify(args)
        );
      }
    });
  }

  private reattachAllListeners() {
    if (!this.socket) return;

    logger.debug("[SocketService] Reattaching all event listeners");

    // Reattach heartbeat listeners
    this.pendingCallbacks.heartbeat.forEach((callback) => {
      this.socket?.on(SOCKET_EVENTS.OPERATOR.HEARTBEAT, callback);
    });
  }

  private handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * 1.5 ** (this.reconnectAttempts - 1); // Changed from 2 to 1.5 for more gradual backoff
      logger.debug(
        `[SocketService] Reconnecting in ${delay}ms... (Attempt ${this.reconnectAttempts})`
      );

      setTimeout(() => {
        logger.debug(
          `[SocketService] Attempting reconnection ${this.reconnectAttempts}`
        );
        this.reconnectIfNeeded();
      }, delay);
    } else {
      logger.error(
        "[SocketService] Max reconnection attempts reached. Please refresh the page."
      );
    }
  }

  private resubscribeAll() {
    logger.debug("[SocketService] Resubscribing to all subscriptions", {
      count: this.subscriptions.size,
      subscriptions: Array.from(this.subscriptions),
    });

    if (this.subscriptions.size > 0) {
      this.subscriptions.forEach((operatorId) => {
        this.doSubscribe(operatorId);
      });
    }
  }

  // Internal method to actually perform the subscription
  private doSubscribe(operatorId: string) {
    if (!this.socket?.connected) {
      logger.warn("[SocketService] Cannot subscribe - socket not connected");
      return false;
    }

    logger.debug("[SocketService] Subscribing to operator:", operatorId);
    this.socket.emit(SOCKET_EVENTS.SUBSCRIBE, {
      type: "operator",
      id: operatorId,
    });
    return true;
  }

  subscribeToOperator(operatorId: string) {
    // Always add to our subscription list regardless of connection status
    this.subscriptions.add(operatorId);

    if (!this.socket?.connected) {
      logger.warn(
        "[SocketService] Socket not connected. Queueing subscription and attempting to reconnect."
      );

      // Attempt to reconnect
      this.reconnectIfNeeded();
      return;
    }

    // Perform the actual subscription
    this.doSubscribe(operatorId);
  }

  /**
   * If the socket is physically connected, disconnect it
   */
  disconnect() {
    // Clear all intervals
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.connectionMonitorInterval) {
      clearInterval(this.connectionMonitorInterval);
      this.connectionMonitorInterval = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      logger.debug("[SocketService] Socket closed.");
    }

    // Clear subscriptions
    this.subscriptions.clear();

    // Clear token
    this.connectionToken = null;
  }

  unsubscribeFromOperator(operatorId: string) {
    // Remove from our tracking set
    this.subscriptions.delete(operatorId);

    if (this.socket?.connected) {
      logger.debug("[SocketService] Unsubscribing from operator:", operatorId);
      this.socket.emit(SOCKET_EVENTS.UNSUBSCRIBE, {
        type: "operator",
        id: operatorId,
      });
    } else {
      logger.warn("[SocketService] Cannot unsubscribe - socket not connected");
    }
  }

  onOperatorHeartbeat(callback: (data: HeartbeatData) => void) {
    // Store callback for reconnection scenarios
    this.pendingCallbacks.heartbeat.add(callback);

    if (!this.socket) {
      logger.warn(
        "[SocketService] Socket not connected when attempting to listen for heartbeat updates"
      );
      this.reconnectIfNeeded();
      return;
    }

    this.socket.on(SOCKET_EVENTS.OPERATOR.HEARTBEAT, callback);
  }

  removeAllListeners() {
    if (this.socket) {
      this.socket.removeAllListeners();
    }

    // Clear stored callbacks
    this.pendingCallbacks.heartbeat.clear();
  }

  getSocket() {
    return this.socket;
  }

  getConnected() {
    return this.socket?.connected ?? false;
  }
}

export default SocketService.getInstance();
